import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  CheckCircle2, XCircle, AlertCircle, Eye, Trash2, FileText, Sparkles,
} from 'lucide-react';
import { usePortalI18n } from '../context/PortalI18nContext';

// ─────────────────────────────────────────────────────────────────────────
// RequiredDocsChecklist
//
// Same UX as RequiredDocsUploader (one row per slug, AI verdict per file),
// but read-only — used by the admin Kanban side-sheet and the portal claim
// detail's snapshot view.
//
// Translations: same hook as the uploader. Outside the portal it falls
// back to English transparently.
// ─────────────────────────────────────────────────────────────────────────
export const RequiredDocsChecklist = ({ claim, docTypeOptions = [], onPreview, onDelete }) => {
  const { t, lang, tDocType } = usePortalI18n();
  const required = claim?.policy?.required_documents || [];
  const descriptionsEn = claim?.policy?.required_documents_descriptions || {};
  const descriptionsHi = claim?.policy?.required_documents_descriptions_hi || {};
  // Iter 36: secretary-facing checklists (auto-generated). Same fallback
  // chain as RequiredDocsUploader — checklist first, admin description
  // second, empty string last.
  const checklistsEn = claim?.policy?.required_documents_user_checklists || {};
  const checklistsHi = claim?.policy?.required_documents_user_checklists_hi || {};
  const pickDesc = (slug) => {
    if (lang === 'hi') {
      return (
        checklistsHi[slug]
        || checklistsEn[slug]
        || descriptionsHi[slug]
        || descriptionsEn[slug]
        || ''
      );
    }
    return checklistsEn[slug] || descriptionsEn[slug] || '';
  };

  const attachments = claim?.attachments || [];
  const perDoc = claim?.ai_review?.per_doc || [];

  // Keyed by attachment_id (preferred) and filename (legacy fallback) so a
  // verdict reliably links to its file even when the AI echoed a different
  // filename — otherwise files hang on a permanent "pending" badge.
  const verdictByFilename = {};
  for (const v of perDoc) {
    if (v.filename && !(v.filename in verdictByFilename)) verdictByFilename[v.filename] = v;
    if (v.attachment_id) verdictByFilename[v.attachment_id] = v;
  }

  const attsBySlug = {};
  for (const a of attachments) {
    const slug = a.doc_type || 'other';
    (attsBySlug[slug] = attsBySlug[slug] || []).push(a);
  }

  const orphanAtts = required.length > 0
    ? attachments.filter((a) => !required.includes(a.doc_type))
    : [];

  const renderVerdictBadge = (verdict, matchedSlug, expectedSlug) => {
    if (!verdict) {
      return (
        <Badge variant="outline" className="text-[10px] gap-1 text-muted-foreground">
          <Sparkles className="h-2.5 w-2.5" />
          {t('docs.aiPending')}
        </Badge>
      );
    }
    if (verdict === 'accepted' && (!expectedSlug || matchedSlug === expectedSlug || !matchedSlug)) {
      return (
        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px] gap-1">
          <CheckCircle2 className="h-2.5 w-2.5" /> {t('docs.accepted')}
        </Badge>
      );
    }
    if (verdict === 'accepted' && matchedSlug && expectedSlug && matchedSlug !== expectedSlug) {
      return (
        <Badge className="bg-amber-600 hover:bg-amber-600 text-[10px] gap-1">
          <AlertCircle className="h-2.5 w-2.5" /> {t('docs.wrongSlugFits', { slug: tDocType(matchedSlug) })}
        </Badge>
      );
    }
    return (
      <Badge className="bg-rose-600 hover:bg-rose-600 text-[10px] gap-1">
        <XCircle className="h-2.5 w-2.5" /> {t('docs.rejected')}
      </Badge>
    );
  };

  const renderRow = (slug, atts, isOrphan = false) => {
    const desc = pickDesc(slug);
    return (
      <div key={slug} className="rounded-lg border bg-white p-3 space-y-2" data-testid={`doc-slug-${slug}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-sm">{tDocType(slug)}</h4>
              {isOrphan ? (
                <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                  {t('docs.extraByPolicyBadge')}
                </Badge>
              ) : atts.length === 0 ? (
                <Badge variant="outline" className="text-[10px] text-rose-700 border-rose-300 gap-1">
                  <XCircle className="h-2.5 w-2.5" /> {t('docs.missingBadge')}
                </Badge>
              ) : null}
            </div>
            {desc && (
              <div className="mt-2 rounded-md border border-emerald-200/70 bg-emerald-50/60 px-3 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800/90 mb-1">
                  {t('docs.checklistHeading')}
                </div>
                <p className="text-xs text-emerald-900/90 leading-relaxed whitespace-pre-line">
                  {desc}
                </p>
                <p className="mt-2 pt-2 border-t border-emerald-300/40 text-[11px] italic text-emerald-800/70 leading-snug">
                  {t('docs.mpcaDisclaimer')}
                </p>
              </div>
            )}
          </div>
        </div>
        {atts.length === 0 ? (
          <div className="text-xs text-rose-700 italic">{t('docs.noFile')}</div>
        ) : (
          atts.map((a) => {
            const v = (a.id && verdictByFilename[a.id]) || verdictByFilename[a.filename];
            return (
              <div
                key={a.id}
                className="flex items-center gap-2 rounded-md bg-muted/30 border px-2 py-1.5 flex-wrap"
                data-testid={`doc-row-${a.id}`}
              >
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-mono text-xs truncate flex-1 min-w-0">{a.filename}</span>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {a.size_bytes ? `${Math.round(a.size_bytes / 1024)} KB` : ''}
                </span>
                {renderVerdictBadge(v?.verdict, v?.matched_slug, isOrphan ? null : slug)}
                <Button size="sm" variant="ghost" onClick={() => onPreview(a)} title={t('common.preview')}>
                  <Eye className="h-4 w-4" />
                </Button>
                {onDelete && (
                  <Button size="sm" variant="ghost" onClick={() => onDelete(a.id)} title={t('common.remove')}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                )}
              </div>
            );
          })
        )}
        {atts.length > 0 && atts.map((a) => {
          const v = verdictByFilename[a.filename];
          if (!v) return null;
          const observation = v.observation || v.summary || '';
          const recommendation = v.recommendation || '';
          if (!observation && !recommendation) return null;
          return (
            <div key={`s-${a.id}`} className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-1 mt-1">
              {observation && (
                <div className="rounded-md bg-sky-50 border border-sky-200 p-2 text-[11px] text-sky-900 leading-snug">
                  <div className="font-semibold mb-0.5">{t('docs.aiObservation')}</div>
                  {observation}
                </div>
              )}
              {recommendation && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900 leading-snug">
                  <div className="font-semibold mb-0.5">{t('docs.aiRecommendation')}</div>
                  {recommendation}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (required.length === 0) {
    return (
      <div className="space-y-2">
        {attachments.map((a) => renderRow(a.doc_type || 'other', [a], false))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {required.map((slug) => renderRow(slug, attsBySlug[slug] || []))}
      {orphanAtts.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-dashed">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t('docs.additionalUploads')}
          </div>
          {orphanAtts.map((a) => renderRow(a.doc_type || 'other', [a], true))}
        </div>
      )}
    </div>
  );
};

export default RequiredDocsChecklist;
